import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquareText, X } from 'lucide-react';
import { fireEvent } from '../../utils/funnelEvents';
import {
  RESEARCH_API,
  getParticipantKey,
  postInviteEvent,
  rememberDismissed,
  wasDismissedRecently, isAutomatedBrowser } from '../../utils/researchParticipant';

// Invitation block for a vertical landing page. Asks the API whether this
// visitor may be invited (frequency rules live server-side), then renders the
// vertical's own copy: what it is, how long it takes, what they get, and the
// sensitive-data warning. Renders nothing until eligibility is known, and
// nothing at all when not eligible, so it never flashes on a page.
//
//   <ResearchInviteCard vertical="healthcare" />
//
// `source` tags where the interview came from (default: the vertical page).
const ResearchInviteCard = ({ vertical, source = 'landing_vertical_page', compact = false }) => {
  const [state, setState] = useState({ status: 'loading', vertical: null, resumeId: null });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (wasDismissedRecently(vertical)) {
      setState({ status: 'hidden', vertical: null, resumeId: null });
      return undefined;
    }
    if (isAutomatedBrowser()) {
      setState({ status: 'hidden', vertical: null, resumeId: null });
      return undefined;
    }
    const pk = getParticipantKey();
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    fetch(`${RESEARCH_API}/invite?vertical=${encodeURIComponent(vertical)}&pk=${encodeURIComponent(pk)}&source=${encodeURIComponent(source)}&path=${encodeURIComponent(path)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data && data.eligible && data.vertical && data.vertical.enabled) {
          setState({ status: 'eligible', vertical: data.vertical, resumeId: data.resumeInterviewId || null });
          fireEvent('research_invite_shown', { vertical, source });
          postInviteEvent(vertical, 'shown', { source });
        } else {
          setState({ status: 'hidden', vertical: null, resumeId: null });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'hidden', vertical: null, resumeId: null });
      });
    return () => {
      cancelled = true;
    };
  }, [vertical, source]);

  // A dismissal from the popup bubble hides this card too.
  useEffect(() => {
    const onDismissed = (e) => {
      if (e && e.detail && e.detail.vertical === vertical) setDismissed(true);
    };
    window.addEventListener('sortio-research-dismissed', onDismissed);
    return () => window.removeEventListener('sortio-research-dismissed', onDismissed);
  }, [vertical]);

  if (state.status !== 'eligible' || dismissed) return null;
  const v = state.vertical;
  const href = `/research/${encodeURIComponent(vertical)}?src=${encodeURIComponent(source)}`;

  const onDismiss = () => {
    setDismissed(true);
    rememberDismissed(vertical);
    fireEvent('research_invite_dismissed', { vertical, source });
    postInviteEvent(vertical, 'dismissed', { source });
  };
  // Plain click opens the interview in a modal on this page (handled by
  // ResearchInvitePopup, mounted in App.js). Middle-click / new tab still
  // gets the standalone page via the href.
  const onAccept = (e) => {
    if (e && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('sortio-research-open', { detail: { vertical, source } }));
    } else {
      fireEvent('research_invite_accepted', { vertical, source });
    }
  };

  return (
    <section className={compact ? 'my-8' : 'my-16'} aria-label="Customer research interview invitation">
      <div className="relative max-w-3xl mx-auto bg-apple-700/30 backdrop-blur-md rounded-3xl border border-apple-600/30 p-6 sm:p-8 overflow-hidden">
        <div className="absolute -right-24 -top-24 w-64 h-64 rounded-full bg-sortio-primary/10 blur-[80px]" />
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute top-4 right-4 text-apple-300 hover:text-apple-50 transition-colors p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-sortio-primary/50"
        >
          <X size={18} />
        </button>
        <div className="relative z-10 flex flex-col sm:flex-row gap-5">
          <div className="shrink-0 w-12 h-12 rounded-2xl bg-sortio-primary/20 flex items-center justify-center">
            <MessageSquareText className="text-sortio-primary" size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wide text-apple-300 mb-1">Customer research</div>
            <h3 className="text-xl sm:text-2xl font-semibold text-apple-50 mb-2 tracking-tight">{v.intro.title}</h3>
            <p className="text-apple-200 text-sm sm:text-base font-light leading-relaxed mb-3">{v.intro.body}</p>
            <ul className="text-sm text-apple-300 space-y-1 mb-4">
              <li>About {v.intro.effortMinutes} minutes, in a chat, whenever suits you. No call to schedule.</li>
              {v.incentive && <li>Finish it and get {v.incentive.description}.</li>}
              {v.prohibitSensitiveData && (
                <li className="text-amber-200/90">{v.intro.privacyNote}</li>
              )}
            </ul>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={href}
                onClick={onAccept}
                className="inline-flex items-center bg-gradient-to-r from-sortio-primary to-sortio-primary/80 text-white font-medium px-5 py-2.5 rounded-xl shadow-lg shadow-sortio-primary/20 hover:brightness-110 transition-all"
              >
                {state.resumeId ? 'Continue the interview' : 'Start the interview'}
              </Link>
              <button type="button" onClick={onDismiss} className="text-sm text-apple-300 hover:text-apple-50 transition-colors">
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResearchInviteCard;
